import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

/**
 * Navigate between CV-builder steps while PRESERVING the existing query params — above all the
 * guest magic-link token `?t=`. The guest session is held only in memory, so keeping the token
 * in the URL is what lets a page reload (or a lapsed-session recovery) re-redeem the still-active
 * grant and resume, instead of dead-ending on the `missing-token` screen. Re-redeeming an active
 * grant is safe server-side (it just mints a fresh session; only submit/expiry are terminal).
 *
 * A bare `navigate('?step=…')` in react-router v6 REPLACES the whole query string and drops `?t=`,
 * which is exactly the bug this avoids. Harmless for the authenticated flow (no `?t=` present).
 */
export const useStepNavigate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  return useCallback(
    (step: string, options?: { edit?: boolean; replace?: boolean }) => {
      const params = new URLSearchParams(searchParams);
      params.set('step', step);
      // `edit` is scoped to the section-edit round-trip: set it only when entering edit mode,
      // clear it on every other transition (Next/Back/Update/Discard back to the summary).
      if (options?.edit) {
        params.set('edit', 'true');
      } else {
        params.delete('edit');
      }
      const search = `?${params.toString()}`;
      // Push by default; only the resume redirect replaces. Keep the plain-push call signature
      // (no options arg) so it matches react-router's default and existing call sites/tests.
      if (options?.replace) {
        navigate(search, { replace: true });
      } else {
        navigate(search);
      }
    },
    [navigate, searchParams]
  );
};
